import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { CreditCard, CheckCircle, ExternalLink, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { getErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STRIPE_JS_SRC = "https://js.stripe.com/v3/";
const STRIPE_SCRIPT_ID = "stripe-js";

// Same dynamic-inject pattern as BookDemoTab's Calendly widget: dedupe by
// script id, resolve once window.Stripe is available.
function loadStripeJs() {
  return new Promise((resolve, reject) => {
    if (window.Stripe) {
      resolve(window.Stripe);
      return;
    }
    let script = document.getElementById(STRIPE_SCRIPT_ID);
    const onLoad = () => (window.Stripe ? resolve(window.Stripe) : reject(new Error("Stripe.js failed to load")));
    const onError = () => reject(new Error("Stripe.js failed to load"));
    if (script) {
      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
      return;
    }
    script = document.createElement("script");
    script.id = STRIPE_SCRIPT_ID;
    script.async = true;
    script.src = STRIPE_JS_SRC;
    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
    document.body.appendChild(script);
  });
}

// Classify a GET /billing/checkout-status response into the three return
// states the tab renders: a completed+paid session unlocks the plan, a
// completed-but-unsettled session (e.g. async payment methods) is pending,
// anything else (open/expired) needs a retry.
function classifyCheckoutReturn(result) {
  if (!result) return null;
  if (result.status === "error") return "error";
  if (result.status === "complete" && result.payment_status === "paid") return "success";
  if (result.status === "complete") return "pending";
  return "retry";
}

export default function BillingTab() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [returnResult, setReturnResult] = useState(null);
  const [returnLoading, setReturnLoading] = useState(false);
  const publishableKeyRef = useRef(null);
  const checkoutContainerRef = useRef(null);
  const embeddedCheckoutRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const subRes = await api.get("/billing/subscription");
        setSubscription(subRes.data);
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to load billing"));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Return from Stripe's embedded checkout: return_url carries
  // ?tab=billing&session_id=... which SettingsPage leaves intact.
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) return;
    setReturnLoading(true);
    api
      .get("/billing/checkout-status", { params: { session_id: sessionId } })
      .then((res) => {
        setReturnResult(res.data);
        if (res.data.status === "complete" && res.data.payment_status === "paid") {
          api
            .get("/billing/subscription")
            .then((r) => setSubscription(r.data))
            .catch(() => {});
        }
      })
      .catch(() => setReturnResult({ status: "error" }))
      .finally(() => {
        setReturnLoading(false);
        // Scrub session_id now that the result has been fetched and will be
        // rendered from component state — otherwise every tab-switch-and-back
        // remounts BillingTab, re-reads session_id from the URL, and shows
        // the return banner forever instead of the normal Upgrade/Manage UI.
        const scrubbed = new URLSearchParams(window.location.search);
        scrubbed.delete("session_id");
        const query = scrubbed.toString();
        const newUrl = `${window.location.pathname}${query ? `?${query}` : ""}`;
        window.history.replaceState({}, "", newUrl);
      });
  }, []);

  // Mount the embedded checkout once a client_secret is available; the
  // container div below renders synchronously on the clientSecret state
  // update, so it exists by the time this effect's async chain resolves.
  useEffect(() => {
    if (!clientSecret) return;
    let cancelled = false;

    loadStripeJs()
      .then((Stripe) => {
        if (cancelled) return null;
        const stripe = Stripe(publishableKeyRef.current);
        return stripe.initEmbeddedCheckout({ clientSecret });
      })
      .then((checkout) => {
        if (!checkout) return;
        if (cancelled) {
          checkout.destroy();
          return;
        }
        embeddedCheckoutRef.current = checkout;
        if (checkoutContainerRef.current) checkout.mount(checkoutContainerRef.current);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load checkout");
          setClientSecret(null);
        }
      });

    return () => {
      cancelled = true;
      if (embeddedCheckoutRef.current) {
        embeddedCheckoutRef.current.destroy();
        embeddedCheckoutRef.current = null;
      }
    };
  }, [clientSecret]);

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      const configRes = await api.get("/billing/config");
      const origin = window.location.origin;
      const res = await api.post("/billing/checkout", {
        plan: "full",
        ui_mode: "embedded",
        return_url: `${origin}/settings?tab=billing&session_id={CHECKOUT_SESSION_ID}`,
      });
      publishableKeyRef.current = configRes.data.publishable_key;
      setClientSecret(res.data.client_secret);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to start checkout"));
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Deep link from every Upgrade/Subscribe entry point (?tab=billing&upgrade=1):
  // open the embedded checkout once billing has loaded, then scrub the param so
  // a remount or refresh doesn't re-open it.
  const autoUpgradeRef = useRef(false);
  useEffect(() => {
    if (loading || autoUpgradeRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") !== "1") return;
    autoUpgradeRef.current = true;
    params.delete("upgrade");
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    if (user?.plan !== "full" && !params.get("session_id")) handleUpgrade();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // "Try again" from a retry/error return: clear the stale return state (the
  // URL's session_id is already scrubbed, so a remount won't re-trigger it)
  // and re-run the same upgrade flow. Clearing returnResult synchronously
  // — before handleUpgrade's async work sets clientSecret — guarantees the
  // checkout container div is on the page (and its ref attached) by the time
  // the mount effect runs.
  const handleRetry = () => {
    setReturnResult(null);
    handleUpgrade();
  };

  const handleCancelCheckout = () => {
    if (embeddedCheckoutRef.current) {
      embeddedCheckoutRef.current.destroy();
      embeddedCheckoutRef.current = null;
    }
    setClientSecret(null);
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.assign(res.data.url);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to open billing portal"));
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await api.post("/billing/cancel", { at_period_end: true });
      toast.success(
        "Subscription cancelled — Full Elementar stays active until the end of this billing period."
      );
      const r = await api.get("/billing/subscription");
      setSubscription(r.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to cancel subscription"));
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-500">Loading billing…</p>
        </CardContent>
      </Card>
    );
  }

  const isFull = user?.plan === "full";
  const hasSub = subscription?.has_subscription === true;
  const subStatus = hasSub ? subscription?.subscription?.status || null : null;
  const cancelsAtPeriodEnd = hasSub && subscription?.subscription?.cancel_at_period_end === true;
  // Backend returns current_period_end as an ISO string (stripe_service.get_subscription_status).
  const periodEnd = hasSub ? subscription?.subscription?.current_period_end : null;
  const periodEndDate = periodEnd ? new Date(periodEnd) : null;
  const cancelDate =
    periodEndDate && !Number.isNaN(periodEndDate.getTime())
      ? periodEndDate.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : null;
  const returnKind = classifyCheckoutReturn(returnResult);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Billing
          </CardTitle>
          <CardDescription>
            {isFull
              ? hasSub
                ? "Full Elementar — active subscription."
                : "Full Elementar — included on your account."
              : "You're on Connections (free) — portals, archive, rules, and Lumos included."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {returnLoading && (
            <div className="rounded-md p-3 bg-gray-50 text-sm text-gray-600">
              Verifying your payment…
            </div>
          )}
          {!returnLoading && returnKind === "success" && (
            <div
              className="flex items-center gap-2 rounded-md p-3"
              style={{ backgroundColor: "#F0FDF4", color: "#166534" }}
            >
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Full Elementar active — features unlock shortly.</span>
            </div>
          )}
          {!returnLoading && returnKind === "pending" && (
            <div className="rounded-md p-3 bg-yellow-50 text-sm text-yellow-700">
              Payment processing — this can take a minute.
            </div>
          )}
          {!returnLoading && (returnKind === "retry" || returnKind === "error") && (
            <div className="rounded-md p-3 bg-red-50 text-sm text-red-700 space-y-2">
              <p>We couldn&apos;t confirm your payment.</p>
              <Button variant="outline" size="sm" onClick={handleRetry} disabled={checkoutLoading}>
                Try again
              </Button>
            </div>
          )}
          {!returnLoading && !returnKind && (
            isFull ? (
              <>
                <div
                  className="flex items-center gap-2 rounded-md p-3"
                  style={{ backgroundColor: "#F0FDF4", color: "#166534" }}
                >
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">
                    Full Elementar is active{subStatus && subStatus !== "active" ? ` (${subStatus})` : ""}.
                  </span>
                </div>
                {hasSub && (
                  <Button
                    variant="outline"
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                    className="rounded-lg"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {portalLoading ? "Opening…" : "Manage subscription"}
                  </Button>
                )}
                {hasSub && cancelsAtPeriodEnd && (
                  <p className="text-sm text-gray-500">
                    {cancelDate ? `Cancels on ${cancelDate}.` : "Cancels at the end of this billing period."}
                  </p>
                )}
                {hasSub && !cancelsAtPeriodEnd && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={cancelLoading}
                        className="block text-gray-500 hover:text-red-700"
                      >
                        Cancel subscription
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                        <AlertDialogDescription>
                          You&apos;ll keep Full Elementar until the end of the current billing
                          period. After that your account moves to Connections (free) — your
                          documents and portals stay.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep subscription</AlertDialogCancel>
                        <AlertDialogAction onClick={handleCancelSubscription}>
                          Cancel subscription
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500">
                  Upgrade to <strong>Full Elementar</strong> to unlock Rosetta — structured
                  extraction, per-fund positions, dashboards, and full-mode Lumos.
                </p>
                {clientSecret ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                      <h3 className="text-sm font-medium text-gray-900">
                        Complete your subscription
                      </h3>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleCancelCheckout}
                        aria-label="Close checkout"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div ref={checkoutContainerRef} />
                  </div>
                ) : (
                  <Button
                    onClick={handleUpgrade}
                    disabled={checkoutLoading}
                    className="text-white rounded-lg"
                    style={{ backgroundColor: "#C41E1E" }}
                    onMouseEnter={(e) =>
                      !checkoutLoading && (e.currentTarget.style.backgroundColor = "#8B1515")
                    }
                    onMouseLeave={(e) =>
                      !checkoutLoading && (e.currentTarget.style.backgroundColor = "#C41E1E")
                    }
                  >
                    {checkoutLoading ? "Loading…" : "Upgrade to Full Elementar — $500/mo"}
                  </Button>
                )}
              </>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
